/**
 * LoginView - Presentational component for the login/signup page
 * @storybook See LoginView.stories.tsx for usage examples
 */

import { t } from "@travily/i18n";
import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import { RetroButton } from "@ui/components/RetroButton";
import { SocialButton } from "@ui/components/SocialButton";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { useHeightBreakpoint } from "@ui/hooks/use-height-breakpoint";
import { Form } from "@ui/primitives/shared";
import { srcSet } from "@ui/utils/image";
import type { ReactNode } from "react";
import bluePanelClouds2x from "./assets/blue-panel-clouds@2x.webp";
import bluePanelClouds3x from "./assets/blue-panel-clouds@3x.webp";
import bluePanelClouds from "./assets/blue-panel-clouds.webp";
import bluePanelCloudsMobile2x from "./assets/blue-panel-clouds-mobile@2x.webp";
import bluePanelCloudsMobile3x from "./assets/blue-panel-clouds-mobile@3x.webp";
import bluePanelCloudsMobile from "./assets/blue-panel-clouds-mobile.webp";
import foundersSignature2x from "./assets/founders-signature@2x.webp";
import foundersSignature3x from "./assets/founders-signature@3x.webp";
import foundersSignature from "./assets/founders-signature.webp";
import hotAirBalloonHero2x from "./assets/hot-air-balloon-hero@2x.webp";
import hotAirBalloonHero3x from "./assets/hot-air-balloon-hero@3x.webp";
import hotAirBalloonHero from "./assets/hot-air-balloon-hero.webp";
import hotAirBalloonHeroMobile2x from "./assets/hot-air-balloon-hero-mobile@2x.webp";
import hotAirBalloonHeroMobile3x from "./assets/hot-air-balloon-hero-mobile@3x.webp";
import hotAirBalloonHeroMobile from "./assets/hot-air-balloon-hero-mobile.webp";
import sparkleIcon2x from "./assets/sparkle-icon@2x.webp";
import sparkleIcon3x from "./assets/sparkle-icon@3x.webp";
import sparkleIcon from "./assets/sparkle-icon.webp";

export interface LoginViewProps {
  alert?: ReactNode;
  csrfToken: string;
  socialProviders: Array<{ id: "google" | "apple" | "facebook"; action: string }>;
  onEmailClick: () => void;
}

export function LoginView({ alert, csrfToken, socialProviders, onEmailClick }: LoginViewProps) {
  const isAboveLg = useBreakpoint("lg");
  const isAboveHLg = useHeightBreakpoint("xl");
  const buttonSize = isAboveLg && isAboveHLg ? "48" : "40";

  return (
    <div className="relative flex w-full max-w-container flex-1 flex-col overflow-hidden rounded-5xl shadow-[inset_0_0_0_1px_var(--color-green-100),inset_0_-1px_0_0_var(--color-green-200)] md:flex-initial md:flex-row md:overflow-visible">
      <div className="relative flex w-full flex-1 flex-col items-center justify-center rounded-t-5xl bg-green-50 px-4 xl:px-8 py-8 sm:py-12 lg:py-6 lg:h-lg:py-8 lg:h-xl:py-10 md:w-[55%] md:flex-initial md:rounded-l-5xl md:rounded-tr-none lg:w-1/2">
        <div className="flex w-full max-w-[560px] flex-col items-center text-center">
          <h1 className="text-display-sm md:text-display-lg lg:text-display-lg lg:h-xl:text-display-xl text-green-950">
            {t("web.logins.show.title")}
          </h1>
          <p className="mt-2 lg:mt-1 text-base md:text-xl lg:text-xl lg:h-xl:text-2xl font-semibold text-green-950">
            {t("web.logins.show.subtitle")}
          </p>
        </div>

        {alert && <div className="mt-4 w-full max-w-[480px]">{alert}</div>}

        <div className="mt-8 md:mt-10 lg:mt-6 lg:h-lg:mt-8 lg:h-xl:mt-10 flex w-full max-w-[480px] flex-col gap-4 md:gap-5">
          {/* i18n-tasks-use t('web.logins.show.social_providers.google') */}
          {/* i18n-tasks-use t('web.logins.show.social_providers.facebook') */}
          {/* i18n-tasks-use t('web.logins.show.continue_with') */}
          {socialProviders.map(({ id, action }) => (
            <Form key={id} action={action} method="post" className="w-full">
              {/* Pre-session: no Inertia in flow yet, so post authenticity_token directly. */}
              <input name="authenticity_token" type="hidden" value={csrfToken} />
              <SocialButton className="w-full" size={buttonSize} social={id} type="submit">
                {t("web.logins.show.continue_with", { provider: t(`web.logins.show.social_providers.${id}`) })}
              </SocialButton>
            </Form>
          ))}

          <RetroButton className="w-full" color="yellow" onPress={onEmailClick} size={buttonSize}>
            {t("web.logins.show.email.button")}
          </RetroButton>
        </div>

        <div className="relative z-10 mt-8 md:mt-10 lg:mt-6 lg:h-lg:mt-8 lg:h-xl:mt-10 mb-[-92px] md:mb-0 w-full max-w-[480px] rounded-3xl bg-white shadow-[0_3px_3px_rgba(0,0,0,0.1),0_1px_1px_rgba(0,0,0,0.05),0_0_0_1px_rgba(0,0,0,0.05)]">
          <div className="flex gap-2 px-3 py-4">
            <img
              src={sparkleIcon}
              srcSet={srcSet(sparkleIcon, sparkleIcon2x, sparkleIcon3x)}
              alt=""
              aria-hidden="true"
              width={28}
              height={28}
              className="size-7 shrink-0 rotate-6"
            />

            <div className="flex flex-col gap-3">
              <p className="pt-1 text-base font-bold text-neutral-700">{t("web.logins.show.founders_note.title")}</p>

              <p className="text-base text-neutral-700 pr-7">
                <span className="h-xl:hidden">{t("web.logins.show.founders_note.body.h_base")}</span>
                <span className="hidden h-xl:inline">{t("web.logins.show.founders_note.body.h_lg")}</span>
              </p>

              <p className="text-base text-neutral-700">
                <img
                  src={foundersSignature}
                  srcSet={srcSet(foundersSignature, foundersSignature2x, foundersSignature3x)}
                  alt={t("web.logins.show.founders_note.signature_alt")}
                  width={156}
                  height={90}
                  className="float-right ml-2 h-12 w-auto -rotate-[5deg] md:h-14 lg:h-12 lg:h-xl:h-14"
                />
                {t("web.logins.show.founders_note.thanks")}
                <br />
                {t("web.logins.show.founders_note.signature")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative w-full pt-[60px] md:pt-0 rounded-b-5xl bg-[rgb(165,215,250)] md:w-[45%] md:overflow-visible md:rounded-bl-none md:rounded-tr-5xl lg:w-1/2">
        <ResponsiveImage
          mobile={{
            src: bluePanelCloudsMobile,
            src2x: bluePanelCloudsMobile2x,
            src3x: bluePanelCloudsMobile3x,
          }}
          desktop={{
            src: bluePanelClouds,
            src2x: bluePanelClouds2x,
            src3x: bluePanelClouds3x,
          }}
          alt=""
          mobileWidth={393}
          mobileHeight={566}
          desktopWidth={343}
          desktopHeight={414}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top rounded-b-5xl md:rounded-bl-none md:rounded-tr-5xl"
          decorative
        />

        <ResponsiveImage
          mobile={{
            src: hotAirBalloonHeroMobile,
            src2x: hotAirBalloonHeroMobile2x,
            src3x: hotAirBalloonHeroMobile3x,
          }}
          desktop={{
            src: hotAirBalloonHero,
            src2x: hotAirBalloonHero2x,
            src3x: hotAirBalloonHero3x,
          }}
          alt={t("web.logins.show.hero_alt")}
          mobileWidth={348}
          mobileHeight={448}
          desktopWidth={690}
          desktopHeight={884}
          className="pointer-events-none relative z-10 block h-auto w-[calc(100%-16px)] object-contain py-6 md:absolute md:top-1/2 md:left-[-8px] lg:left-[-16px] xl:left-[-32px] md:z-50 md:w-[calc(100%+8px)] lg:w-[calc(100%+16px)] xl:w-[calc(100%+32px)] md:max-w-none md:min-h-[450px] lg:min-h-[400px] lg:h-xl:min-h-[600px] md:max-h-[calc(100dvh-88px-32px)] md:py-0 md:animate-float"
          fetchPriority="high"
        />
      </div>
    </div>
  );
}
